<script lang="ts">
  import { debounce, map } from 'lodash-es'
  import { getContext } from 'svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { onChange } from '#app/lib/svelte/svelte'
  import { treq } from '#app/lib/treq'
  import Spinner from '#components/spinner.svelte'
  import type { ItemsSearchFilters } from '#inventory/types/inventory_browser'
  import type { ControllersAPI } from '#server/types/controllers_api'
  import type { ItemId } from '#server/types/item'
  import { I18n } from '#user/lib/i18n'

  let {
    textFilterItemsIds = $bindable(),
    flash = $bindable(),
  }: {
    textFilterItemsIds: ItemId[]
    flash?: FlashState
  } = $props()

  const { ownerId, groupId, shelfId } = getContext('items-search-filters')() as ItemsSearchFilters

  type ItemSearchController = ControllersAPI['items']['get']['search']

  let textFilter: string = $state()
  let waiting: Promise<ItemSearchController['response']> = $state()

  async function search () {
    try {
      if (!textFilter) {
        // Use 'undefined' rather than 'null', to not trigger reactive blocks
        // when the previous value was already undefined
        textFilterItemsIds = undefined
        return
      }
      waiting = treq('items', 'get', 'search', buildQuery())
      const { items } = await waiting
      textFilterItemsIds = map(items, '_id')
    } catch (err) {
      flash = err
    }
  }

  function buildQuery (): ItemSearchController['params'] {
    const base = {
      search: textFilter,
      limit: 100,
    }
    if (groupId) return { group: groupId, ...base }
    if (shelfId) return { shelf: shelfId, ...base }
    else return { user: ownerId, ...base }
  }

  const lazySearch = debounce(search, 200)

  $effect(() => {
    onChange(textFilter, lazySearch)
  })

  const reset = () => textFilter = ''

  function onKeyDown (e) {
    const key = getActionKey(e)
    if (key === 'esc') reset()
  }
</script>

<div class="wrapper">
  <input
    type="text"
    placeholder={I18n('search_verb')}
    bind:value={textFilter}
    onkeydown={onKeyDown}
  />
  <div class="search-icon">
    {#await waiting}
      <Spinner />
    {:then}
      {#if textFilter}
        <button
          title={I18n('reset filter')}
          onclick={reset}
        >
          {@html icon('close')}
        </button>
      {:else}
        {@html icon('search')}
      {/if}
    {/await}
  </div>
</div>

<style lang="scss">
  @import "general/scss/utils";
  .wrapper{
    align-self: flex-end;
    position: relative;
  }
  .search-icon{
    position: absolute;
    inset-inline-end: 0.5em;
    inset-block-start: 0.35em;
    color: $grey;
    button{
      padding: 0.2em 0 0.2em 0.2em;
      margin: 0;
      @include shy;
    }
  }
  input{
    margin: 0;
    padding: 0 0.5em;
    height: 2.1rem;
    @include radius;
  }
</style>
